import { useCallback, useEffect, useState } from "react";

export const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;

const getCurrentVersion = () => process.env.REACT_APP_COMMIT_SHA;

export const isVersionCheckEnabled = (sha = getCurrentVersion()) =>
  Boolean(sha) && sha !== "local";

/**
 * Polls /version.json and reports when the deployed build differs from the
 * one running in this tab. Never throws; failures are silently ignored.
 */
export default function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const check = useCallback(async () => {
    const current = getCurrentVersion();
    if (!isVersionCheckEnabled(current)) return;
    try {
      const res = await fetch("/version.json", { cache: "no-store" });
      if (!res.ok) return;
      const data = await res.json();
      if (data && typeof data.version === "string" && data.version && data.version !== current) {
        setUpdateAvailable(true);
      }
    } catch {
      // A failed check must never break the app.
    }
  }, []);

  useEffect(() => {
    if (!isVersionCheckEnabled()) return undefined;
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    const id = setInterval(check, VERSION_CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
    };
  }, [check]);

  return { updateAvailable };
}
